import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaPaperPlane, FaUserCircle, FaPaperclip, FaDownload, FaEnvelope, FaFilter } from 'react-icons/fa';
import { MdGroup, MdPerson, MdSchool, MdSupervisorAccount } from 'react-icons/md';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatDate } from '../../utils/helpers';

/**
 * Message Center component for parents to communicate with administrators, teachers and mentors
 */
const MessageCenter = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, [filter, searchTerm]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation._id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Fetch all conversations
   */
  const fetchConversations = async () => {
    try {
      setLoading(true);
      
      // Prepare query params for filtering
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('type', filter);
      if (searchTerm) params.append('search', searchTerm);
      
      const queryString = params.toString();
      const endpoint = `/parents/messages/conversations${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(endpoint);
      setConversations(response.data.data || []);
      
      // Select the first conversation if available and none is selected
      if (response.data.data.length > 0 && !selectedConversation) {
        setSelectedConversation(response.data.data[0]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      showToast('Failed to load conversations', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch messages for a specific conversation
   */
  const fetchMessages = async (conversationId) => {
    try {
      setLoading(true);
      const response = await api.get(`/parents/messages/conversations/${conversationId}`);
      setMessages(response.data.data.messages || []);
      
      // Mark conversation as read
      await api.put(`/parents/messages/conversations/${conversationId}/read`);
      
      // Update conversations list to reflect read status
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv._id === conversationId ? { ...conv, unreadCount: 0 } : conv
        )
      );
      
      // Focus on message input
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      showToast('Failed to load messages', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle selecting a conversation
   */
  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
  };

  /**
   * Send a new message in the current conversation
   */
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedConversation) return;
    
    try {
      const response = await api.post(`/parents/messages/conversations/${selectedConversation._id}/messages`, {
        content: newMessage.trim()
      });
      
      // Add new message to local state
      setMessages(prevMessages => [...prevMessages, response.data.data]);
      
      // Update the conversation in the list
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv._id === selectedConversation._id 
            ? {
                ...conv,
                lastMessage: newMessage.trim(),
                lastMessageDate: new Date().toISOString()
              } 
            : conv
        )
      );
      
      // Clear input
      setNewMessage('');
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      showToast('Failed to send message', 'error');
    }
  };

  /**
   * Handle search input change
   */
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  /**
   * Handle download attachment
   */
  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await api.get(`/messages/attachments/${attachmentId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showToast('Failed to download attachment', 'error');
    }
  };

  /**
   * Format date for display
   */
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // If today, show only time
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // If yesterday, show "Yesterday" and time
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    // Otherwise show full date
    return date.toLocaleDateString([], { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  /**
   * Get sender name from conversation
   */
  const getSenderName = (conversation) => {
    if (!conversation || !conversation.sender) return 'Unknown';
    
    if (conversation.sender.role === 'system_admin') {
      return `System Admin (${conversation.sender.firstName} ${conversation.sender.lastName})`;
    } else if (conversation.sender.role === 'school_admin') {
      return `${conversation.sender.school?.name || 'School'} Admin (${conversation.sender.firstName} ${conversation.sender.lastName})`;
    } else if (conversation.sender.role === 'teacher') {
      return `Teacher ${conversation.sender.firstName} ${conversation.sender.lastName}`;
    } else if (conversation.sender.role === 'mentor') {
      return `Mentor ${conversation.sender.firstName} ${conversation.sender.lastName}`;
    }
    
    return `${conversation.sender.firstName} ${conversation.sender.lastName}`;
  };

  /**
   * Get icon for sender role
   */
  const getSenderIcon = (role) => {
    switch (role) {
      case 'system_admin':
        return <MdSupervisorAccount className="text-red-500" />;
      case 'school_admin':
        return <MdSchool className="text-blue-500" />;
      case 'teacher':
        return <MdPerson className="text-green-500" />;
      case 'mentor':
        return <MdPerson className="text-purple-500" />;
      default:
        return <MdPerson className="text-gray-500" />;
    }
  };

  /**
   * Scroll to the bottom of the message list
   */
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Message Center</h1>
          <p className="text-gray-600">Communicate with teachers, mentors and school administrators</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-3">
          {/* Conversations List */}
          <div className="border-r border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex space-x-2 mb-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search messages..."
                    className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  />
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
              <select
                value={filter}
                onChange={handleFilterChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              >
                <option value="all">All Messages</option>
                <option value="unread">Unread</option>
                <option value="system_admin">System Admin</option>
                <option value="school_admin">School Admin</option>
                <option value="teacher">Teachers</option>
                <option value="mentor">Mentors</option>
              </select>
            </div>
            
            <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
              {loading && conversations.length === 0 ? (
                <div className="p-4 text-center">
                  <LoadingSpinner />
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FaEnvelope className="mx-auto text-4xl mb-2 text-gray-300" />
                  <p>No messages found</p>
                </div>
              ) : (
                conversations.map(conversation => (
                  <div
                    key={conversation._id}
                    onClick={() => selectConversation(conversation)}
                    className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition ${
                      selectedConversation?._id === conversation._id ? 'bg-blue-50' : ''
                    } ${conversation.unreadCount > 0 ? 'font-semibold bg-blue-50' : ''}`}
                  >
                    <div className="flex items-start">
                      <div className="mr-3">
                        {conversation.sender?.profileImage ? (
                          <img 
                            src={conversation.sender.profileImage} 
                            alt={`${conversation.sender.firstName} ${conversation.sender.lastName}`}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            {getSenderIcon(conversation.sender?.role)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between">
                          <p className="font-medium truncate">
                            {getSenderName(conversation)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(conversation.lastMessageDate)}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600 truncate">
                          {conversation.subject}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {conversation.lastMessage}
                        </p>
                      </div>
                      {conversation.unreadCount > 0 && (
                        <div className="ml-2 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                          {conversation.unreadCount}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Messages View */}
          <div className="col-span-2 flex flex-col h-full" style={{ height: '70vh' }}>
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center">
                    {selectedConversation.sender?.profileImage ? (
                      <img 
                        src={selectedConversation.sender.profileImage} 
                        alt={selectedConversation.sender.name}
                        className="w-10 h-10 rounded-full object-cover mr-3"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                        {getSenderIcon(selectedConversation.sender?.role)}
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium">{getSenderName(selectedConversation)}</h3>
                      <p className="text-sm text-gray-500">{selectedConversation.subject}</p>
                    </div>
                  </div>
                </div>
                
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50" style={{ maxHeight: 'calc(70vh - 140px)' }}>
                  {loading && messages.length === 0 ? (
                    <div className="text-center py-10">
                      <LoadingSpinner />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                      <p>No messages in this conversation yet.</p>
                    </div>
                  ) : (
                    messages.map(message => (
                      <div
                        key={message._id}
                        className={`mb-4 flex ${message.sender._id === user?._id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-3/4 rounded-lg px-4 py-2 ${
                            message.sender._id === user?._id
                              ? 'bg-primary text-white rounded-br-none'
                              : 'bg-white border border-gray-200 rounded-bl-none'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <p className={`text-xs ${message.sender._id === user?._id ? 'text-blue-100' : 'text-gray-500'}`}>
                              {message.sender._id === user?._id ? 'You' : message.sender.firstName}
                            </p>
                            <p className={`text-xs ml-4 ${message.sender._id === user?._id ? 'text-blue-100' : 'text-gray-500'}`}>
                              {formatDateTime(message.createdAt)}
                            </p>
                          </div>
                          <p>{message.content}</p>
                          
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-2">
                              {message.attachments.map(attachment => (
                                <div 
                                  key={attachment._id} 
                                  className={`flex items-center mt-1 p-1 rounded ${
                                    message.sender._id === user?._id ? 'bg-blue-400' : 'bg-gray-100'
                                  }`}
                                >
                                  <FaPaperclip className={message.sender._id === user?._id ? 'text-white' : 'text-gray-500'} />
                                  <span 
                                    className={`text-sm ml-1 truncate ${message.sender._id === user?._id ? 'text-white' : 'text-gray-600'}`}
                                  >
                                    {attachment.filename}
                                  </span>
                                  <button
                                    onClick={() => handleDownloadAttachment(attachment._id, attachment.filename)}
                                    className={`ml-2 text-sm ${
                                      message.sender._id === user?._id ? 'text-blue-100 hover:text-white' : 'text-blue-500 hover:text-blue-700'
                                    }`}
                                  >
                                    <FaDownload />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Message Input */}
                <div className="p-4 border-t border-gray-200">
                  <form onSubmit={handleSendMessage} className="flex">
                    <input
                      type="text"
                      ref={messageInputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 rounded-l-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-r-md disabled:opacity-50"
                    >
                      <FaPaperPlane />
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <FaEnvelope className="text-5xl mb-4 text-gray-300" />
                <p>Select a conversation to view messages</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageCenter; 